import styled from 'styled-components';
import { NavLink } from '@remix-run/react';
import { fontFamilies } from '~/theme/typography';
import { Colors } from '~/theme';

export const Root = styled.div`
  && {
    min-height: 100svh;
    background-color: ${({ theme }) => theme.colors.white};
    position: relative;
    // min-height: -webkit-fill-available;
  }
`;

export const ContentWrapper = styled.div`
  position: relative;
  height: 100%;
`;

export const NavigationRow = styled.div`
  display: flex;
  justify-content: space-between;
  height: 1.5625rem;
  width: 100%;
  position: fixed;
  background-color: transparent;
  z-index: ${({ theme }) => theme.zIndex.header};
  align-items: center;
`;

export const NavigationItem = styled(NavLink)<{
  color?: string;
  fontColorHover?: string;
  fontWeight?: number;
}>`
  text-align: left;
  text-decoration: none;
  text-transform: uppercase;
  color: ${(props) => props.color ?? props.theme.colors.primaryDark};
  cursor: pointer;
  display: block;
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : 900)};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4475rem;
  letter-spacing: 0.0094rem;
  transition: color 0.2s ease-in;

  &:hover {
    color: ${({ theme, fontColorHover }) =>
      fontColorHover ?? theme.colors.textHover};
  }

  &.active {
    color: ${({ theme }) => theme.colors.textActive};
  }
`;

export const MainContentWrapper = styled.main`
  padding: 0 5.625rem;

  @media ${({ theme }) => theme.breakpoints.md} {
    padding: 0 2rem;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 0 1rem;
  }
`;

export const StyledFlashSaleWrapper = styled.main<{ flashSaleColor?: string }>`
  min-height: 100svh;
  background-color: ${({ flashSaleColor }) =>
    flashSaleColor ? flashSaleColor : '#ce8498'};
  display: grid;
  align-content: center;

  * {
    font-family: ${fontFamilies.AutomateRegular};
  }
`;

export const FlashSaleTimerWrapper = styled.div`
  margin-top: 86px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;

  .flash-sale-timer-title {
    text-transform: uppercase;
    font-size: 34px;
  }
`;

export const FlashSaleTimer = styled.div`
  font-size: 32px;
  font-family: ${fontFamilies.AutomateBold};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    font-size: 64px;
  }
`;

export const FlashSaleProductTitle = styled.h1<{ color?: Colors }>`
  @media (max-width: ${({ theme }) => theme.breakpointValues.md}px) {
    display: none;
    font-size: 1.5rem;
  }

  font-size: 1.3rem;
  text-transform: uppercase;
  line-height: 28px;
  margin-bottom: 40px;
  font-family: ${fontFamilies.AutomateRegular};
  color: ${({ color, theme }) => theme.colors[color ?? 'white']};
`;

export const FlashSaleProductTitleMobile = styled(FlashSaleProductTitle)<{
  color?: Colors;
}>`
  @media (min-width: ${({ theme }) => theme.breakpointValues.md}px) {
    display: none;
  }

  display: block;
  margin-bottom: 8px;
  font-family: ${fontFamilies.AutomateRegular};
  color: ${({ color, theme }) => theme.colors[color ?? 'white']};
`;

export const FlashSaleProductGrid = styled.div`
  /* For mobile: stack vertically */
  display: grid;
  justify-content: center;
  grid-template-columns: 1fr;
  grid-template-areas:
    'image'
    'purchase'
    'description';

  /* For tablet and above: 3-column layout */
  @media (min-width: ${({ theme }) => theme.breakpointValues.md}px) {
    padding: 24px 40px;
    gap: 80px;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas: 'description image purchase';
  }

  /* Optional: for very large screens, cap the column widths */
  @media (min-width: ${({ theme }) => theme.breakpointValues.lg}px) {
    grid-template-columns: minmax(250px, 320px) minmax(250px, 660px) minmax(
        250px,
        320px
      );
    grid-template-areas: 'description image purchase';
  }

  padding: 24px 16px;
  gap: 40px;
  align-items: start;
`;

export const FlashSaleProductDescription = styled.div<{ color?: Colors }>`
  padding-top: 100px;
  grid-area: description;
  color: ${({ color, theme }) => theme.colors[color ?? 'white']};
  font-size: 14px;
  letter-spacing: 0.15px;
  line-height: 20px;

  @media ${({ theme }) => theme.breakpoints.md} {
    padding-top: 0;
  }

  li,
  p {
    margin-bottom: 8px;
  }
`;

export const FlashSaleProductImage = styled.div`
  grid-area: image;
  justify-self: center;

  img {
    max-width: 600px;
    min-width: 268px;
    width: 100%;
    height: auto;
  }
`;

export const FlashSaleProductPurchase = styled.div<{ color?: Colors }>`
  grid-area: purchase;
  padding-top: 170px;
  color: ${({ color, theme }) => theme.colors[color ?? 'white']};
  font-size: 14px;
  letter-spacing: 0.15px;
  line-height: 20px;

  @media ${({ theme }) => theme.breakpoints.md} {
    padding-top: 0;
  }

  .flash-sale-price {
    font-size: 1.3rem;
    margin-bottom: 40px;
    display: flex;

    @media (max-width: ${({ theme }) => theme.breakpointValues.md}px) {
      font-size: 1.5rem;
    }

    span {
      font-family: ${fontFamilies.AutomateBold};
    }

    > img {
      margin-right: 4px;
    }
  }

  .flash-sale-option {
    margin-bottom: 40px;

    .flash-sale-option-title {
      font-size: 16px;
      margin-bottom: 8px;
      text-transform: uppercase;
      font-family: ${fontFamilies.AutomateRegular};
    }

    .flash-sale-option-content {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
    }
  }

  .flash-sale-color {
    margin-bottom: 40px;

    .flash-sale-color-title {
      font-size: 16px;
      text-transform: uppercase;
      margin-bottom: 8px;
      font-family: ${fontFamilies.AutomateBold};
    }
  }

  .flash-sale-size {
    margin-bottom: 40px;

    @media (max-width: ${({ theme }) => theme.breakpointValues.md}px) {
      width: 100%;
    }

    .flash-sale-size-title {
      font-size: 16px;
      text-transform: uppercase;
      font-family: ${fontFamilies.AutomateBold};
    }

    .flash-sale-size-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
  }
`;
